// A product's specifications as the rows of its table: the meaningless
// ones left out, each metric one row over all its statements, the curated
// metrics apart from the rest. Pure functions: the table draws what they
// return.
//
// Every rule reads the normalised fields only (the key, the unit, the
// numbers), never the wording, which is in the document's language.

import {
  FactSource,
  ProductSpecification,
  SpecificationStatement,
} from "../../types/productPlane";
import { formatSpecificationValue } from "./format";
import { DisplayUnit, displayUnit, statementNumbers } from "./unitScale";

// Why a specification is left out of the table
export type OmittedReason = "relative" | "no value";

export interface SpecificationRow {
  specification: ProductSpecification;
  // Its statements with a value, newest first
  statements: SpecificationStatement[];
  // The value of the row: the one value of its statements, else the range
  // they span
  value: Pick<SpecificationStatement, "value_min" | "value_max" | "value_text">;
  // The unit the row and its statements read in: 250000 m reads 250 km
  display: DisplayUnit;
  // The qualifier its statements share; none where they differ
  qualifier: string | null;
  // The documents of all its statements, each once
  sources: FactSource[];
  // Whether its statements give different values
  differs: boolean;
  // Why it is left out, for a row of the left out
  omitted?: OmittedReason;
}

export interface SpecificationModel {
  // The metrics of the curated registry, which products are compared by
  curated: SpecificationRow[];
  // The metrics that came verbatim from documents
  other: SpecificationRow[];
  // The specifications left out: relative values, or no value at all
  omitted: SpecificationRow[];
}

// A unit that makes a value relative to something unnamed: "20 times"
const RELATIVE_UNIT = /^-?(times|x|×|fold)$/i;

// A key that names a change rather than a quantity: "cost reduction",
// "accuracy improvement factor", "speed increase"
const RELATIVE_KEY = /[ _](increase|reduction|improvement|decrease|savings|boost|factor)$/;

/** Whether a specification states a value relative to something unnamed. */
export const isRelative = (specification: ProductSpecification): boolean =>
  RELATIVE_UNIT.test((specification.unit ?? "").trim()) ||
  RELATIVE_KEY.test(specification.key);

/** Whether a statement holds no value: no number and no text. */
export const hasNoValue = (statement: SpecificationStatement): boolean =>
  statement.value_min === null &&
  statement.value_max === null &&
  !statement.value_text?.trim();

// A statement's value as the table shows it, in the row's unit: two values
// that round to the same figures do not differ
const shownValue = (statement: SpecificationStatement, display: DisplayUnit): string =>
  formatSpecificationValue(statement, display.divisor);

const uniqueSources = (statements: SpecificationStatement[]): FactSource[] => {
  const byDocument = new Map<string, FactSource>();
  for (const source of statements.flatMap((statement) => statement.sources)) {
    if (!byDocument.has(source.document_id)) {
      byDocument.set(source.document_id, source);
    }
  }
  return Array.from(byDocument.values());
};

// The value of several statements: the range their numbers span, from the
// lowest bound to the highest; the newest text where none has a number
const spannedValue = (statements: SpecificationStatement[]): SpecificationRow["value"] => {
  if (statements.length === 1) {
    const [only] = statements;
    return { value_min: only.value_min, value_max: only.value_max, value_text: only.value_text };
  }
  const lows = statements.flatMap((statement) => {
    const low = statement.value_min ?? statement.value_max;
    return low === null ? [] : [low];
  });
  const highs = statements.flatMap((statement) => {
    const high = statement.value_max ?? statement.value_min;
    return high === null ? [] : [high];
  });
  if (lows.length === 0) {
    return { value_min: null, value_max: null, value_text: statements[0].value_text };
  }
  return { value_min: Math.min(...lows), value_max: Math.max(...highs), value_text: null };
};

/** One row of a specification over the given statements of it. */
export const specificationRow = (
  specification: ProductSpecification,
  statements: SpecificationStatement[],
): SpecificationRow => {
  const qualifiers = new Set(statements.map((statement) => statement.qualifier));
  const display = displayUnit(specification.unit, statementNumbers(statements));
  return {
    specification,
    statements,
    value: spannedValue(statements),
    display,
    qualifier: qualifiers.size === 1 ? statements[0].qualifier : null,
    sources: uniqueSources(statements),
    differs: new Set(statements.map((statement) => shownValue(statement, display))).size > 1,
  };
};

/**
 * A product's specifications as rows. A specification of relative values
 * ("20 times higher accuracy", "cost reduction 30 %") is left out, as is a
 * statement with no value, and so a specification with none. The others
 * are one row each, the curated metrics apart from the rest.
 */
export const specificationModel = (
  specifications: ProductSpecification[],
): SpecificationModel => {
  const model: SpecificationModel = { curated: [], other: [], omitted: [] };
  for (const specification of specifications) {
    const valued = specification.statements.filter((statement) => !hasNoValue(statement));
    if (isRelative(specification)) {
      model.omitted.push({
        ...specificationRow(specification, specification.statements),
        omitted: "relative",
      });
    } else if (valued.length === 0) {
      model.omitted.push({
        ...specificationRow(specification, specification.statements),
        omitted: "no value",
      });
    } else {
      (specification.curated ? model.curated : model.other).push(
        specificationRow(specification, valued),
      );
    }
  }
  return model;
};
